import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { AppCard, LoadingDots } from "../../components/ui";
import { Component } from "react";
import { InputSwitch } from "primereact/inputswitch";
import { toastService } from "../../services/toastService";
import { clientApiService } from "../../services/api/clients/ClientApiService";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { confirmAction } from "../../utils/confirmHelpers";
import { merchantConfigrationsApiService } from "../../services/api/merchants/MerchantConfigrations";
import { Merchant } from "../../models/MerchantModel";
import { rolesApiService } from "../../services/api/users/RolesApiService";
import { RoleModel } from "../../models/Role";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Plus, X } from "lucide-react";

interface MerchantClientProps {
    merchantId: number | undefined;
    merchant: Merchant;
    isExternalKyc: boolean;
    isBtrustAppActive: boolean;
    isNewRegistrationActive: boolean;
    allowMultiMerchantIban: boolean;
    allowMultiMerchantClientIban: boolean;
    isKycOptional: boolean;
    isPepDeclaration: boolean;
    isBillTariffActive: boolean;
    isCardIssuer: boolean;
    onChangePepDeclarationSwitch: (value: boolean) => void;
    onChangeKycSwitch: (value: boolean) => void;
    onChangeBtrustAppSwitch: (value: boolean) => void;
    onChangeNewRegistrationSwitch: (value: boolean) => void;
    onChangeMultiMerchantIbanSwitch: (value: boolean) => void;
    onChangeMultiMerchantClientIbanSwitch: (value: boolean) => void;
    onChangeisKycOptionalSwitch: (value: boolean) => void;
    onChangeBillTariffSwitch: (value: boolean) => void;
    onChangeCardIssuerSwitch: (value: boolean) => void;
}

interface MerchantClientState {
    hasPortalAccess: boolean;
    hasMtlsAccess: boolean;
    hasOidcAccess: boolean;
    isLoading: boolean;
    // MTLS related
    showMtlsDialog: boolean;
    mtlsCertificateValue: string;
    mtlsErrorMessage: string;
    availableRoles: RoleModel[];
    selectedRole: RoleModel | null;
    // OIDC related
    showOidcDialog: boolean;
    oidcErrorMessage: string;
    redirectUrls: string[];
    postLogoutUrls: string[];
    corsOrigins: string[];
    hasExternalKycAccess: boolean;
    hasBtrustAppActive: boolean;
    hasNewRegistrationActive: boolean;
}

export default class MerchantClientComponent extends Component<MerchantClientProps, MerchantClientState> {
    constructor(props: MerchantClientProps | Readonly<MerchantClientProps>) {
        super(props);
        this.state = {
            hasPortalAccess: false,
            hasMtlsAccess: false,
            hasOidcAccess: false,
            isLoading: true,
            // MTLS
            showMtlsDialog: false,
            mtlsCertificateValue: "",
            mtlsErrorMessage: "",
            availableRoles: [],
            selectedRole: null,
            // OIDC
            showOidcDialog: false,
            oidcErrorMessage: "",
            redirectUrls: [""],
            postLogoutUrls: [""],
            corsOrigins: [""],
            hasExternalKycAccess: this.props.isExternalKyc,
            hasBtrustAppActive: this.props.isBtrustAppActive,
            hasNewRegistrationActive: this.props.isNewRegistrationActive,
        };
    }

    async componentDidMount() {
        await this.loadInitialMerchantPortalAccess();
    }

    reject = () => {
        toastService.warn("You have rejected", "Rejected");
    };
    loadInitialMerchantPortalAccess = async () => {
        try {
            this.setState({ isLoading: true });
            const merchantResult = await clientApiService.GetMerchantAccess({
                merchantId: this.props.merchantId!,
            });
            if (!merchantResult.success) {
                this.setState({ isLoading: false });
                toastService.error(merchantResult.message);
                return;
            }
            this.setState({
                hasPortalAccess: merchantResult.data.hasPortalAccess,
                hasMtlsAccess: merchantResult.data.hasApiAccess,
                hasOidcAccess: merchantResult.data.hasOidcAccess || false,
                isLoading: false
            });
        } catch (error) {
            this.setState({ isLoading: false });
        }
    };

    loadAvailableRoles = async () => {
        try {
            if (!this.props.merchantId) {
                toastService.error("Merchant ID is not available");
                return;
            }
            const rolesResult = await rolesApiService.getAllByMerchant(this.props.merchantId);
            if (!rolesResult.success) {
                toastService.error(rolesResult.message);
                return;
            }
            this.setState({ availableRoles: rolesResult.data });
        } catch (error) {
            toastService.error("Failed to load roles");
        }
    };

    handlePortalAccessChange = async (e: { value: boolean }) => {
        try {
            if (e.value == true) {
                this.setState({ isLoading: true });
                const merchantResult = await clientApiService.AddMerchantPortalAccess({
                    merchantId: this.props.merchantId!,
                });

                if (!merchantResult.success) {
                    this.setState({ isLoading: false });
                    toastService.error(merchantResult.message);
                    return;
                }
                this.setState({ hasPortalAccess: e.value, isLoading: false });
                window.location.reload();
            } else {
                this.setState({ isLoading: true });
                const merchantResult = await clientApiService.RemoveMerchantPortalAccess(this.props.merchantId!);

                if (!merchantResult.success) {
                    this.setState({ isLoading: false });
                    toastService.error(merchantResult.message);
                    return;
                }
                this.setState({ hasPortalAccess: e.value, isLoading: false });
                window.location.reload();
            }

            // Handle success case if necessary
        } catch (error) {
            this.setState({ isLoading: false });
        }
    };
    handleMtlsAccessChange = async (e: { value: boolean }) => {
        try {
            if (e.value === true) {
                this.loadAvailableRoles();
                this.setState({ showMtlsDialog: true, mtlsErrorMessage: "", selectedRole: null });
            } else {
                confirmAction(
                    "Are you sure you want to remove MTLS access? This will delete the MTLS client configuration.",
                    "Confirmation",
                    () => {
                        this.removeMtlsAccess();
                    },
                );
            }
        } catch (error) {
            this.setState({ isLoading: false });
        }
    };

    handleOidcAccessChange = async (e: { value: boolean }) => {
        try {
            if (e.value === true) {
                this.setState({ showOidcDialog: true, oidcErrorMessage: "" });
            } else {
                confirmAction(
                    "Are you sure you want to remove OIDC access? This will delete the OIDC client configuration.",
                    "Confirmation",
                    () => {
                        this.removeOidcAccess();
                    },
                );
            }
        } catch (error) {
            this.setState({ isLoading: false });
        }
    };

    removeMtlsAccess = async () => {
        this.setState({ isLoading: true });
        const merchantResult = await clientApiService.RemoveMerchantApiAccess(this.props.merchantId!);

        if (!merchantResult.success) {
            this.setState({ isLoading: false });
            toastService.error(merchantResult.message);
            return;
        }

        this.setState({ hasMtlsAccess: false, isLoading: false });
        toastService.success("MTLS access removed successfully");
        window.location.reload();
    };

    removeOidcAccess = async () => {
        this.setState({ isLoading: true });
        const merchantResult = await clientApiService.RemoveMerchantOidcAccess(this.props.merchantId!);

        if (!merchantResult.success) {
            this.setState({ isLoading: false });
            toastService.error(merchantResult.message);
            return;
        }

        this.setState({ hasOidcAccess: false, isLoading: false });
        toastService.success("OIDC access removed successfully");
        window.location.reload();
    };

    titleTemplate = (rowData: any) => {
        return (
            <>
                <h6>Merchant has Access to: </h6>
            </>
        );
    };
    MtlsAccessTemplate = (rowData: any) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }}>
                <div style={{ flex: 1 }}>
                    <InputSwitch checked={this.state.hasMtlsAccess} onChange={this.handleMtlsAccessChange} />
                </div>
            </div>
        );
    };

    OidcAccessTemplate = (rowData: any) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }}>
                <div style={{ flex: 1 }}>
                    <InputSwitch checked={this.state.hasOidcAccess} onChange={this.handleOidcAccessChange} />
                </div>
            </div>
        );
    };
    merchantPortalTemplate = (rowData: any) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }}>
                <div style={{ flex: 1 }}>
                    <InputSwitch checked={this.state.hasPortalAccess} onChange={this.handlePortalAccessChange} />
                </div>
            </div>
        );
    };

    handleMtlsSubmit = async () => {
        try {
            if (!this.state.selectedRole) {
                toastService.warn("Please select a role");
                return;
            }

            this.setState({ isLoading: true });

            const merchantResult = await clientApiService.AddMerchantApiAccess({
                merchantId: this.props.merchantId!,
                merchantAccountRoleName: this.state.selectedRole.name,
            });

            if (!merchantResult.success) {
                this.setState({ isLoading: false, mtlsErrorMessage: merchantResult.message });
                toastService.error(merchantResult.message);
                setTimeout(() => {
                    this.setState({ mtlsErrorMessage: "" });
                }, 3000);
                return;
            }

            this.setState({
                hasMtlsAccess: true,
                isLoading: false,
                mtlsCertificateValue: merchantResult.data!.certificate
            });
            toastService.success("MTLS client created successfully");
        } catch (error) {
            this.setState({ showMtlsDialog: false, isLoading: false, mtlsCertificateValue: "" });
            toastService.error("Failed to create MTLS client");
        }
    };

    handleOidcSubmit = async () => {
        try {
            const validRedirectUrls = this.state.redirectUrls.filter(url => url.trim() !== "");
            const validPostLogoutUrls = this.state.postLogoutUrls.filter(url => url.trim() !== "");
            const validCorsOrigins = this.state.corsOrigins.filter(origin => origin.trim() !== "");

            if (validRedirectUrls.length === 0 || validPostLogoutUrls.length === 0 || validCorsOrigins.length === 0) {
                toastService.warn("Please provide at least one value for each field");
                return;
            }

            this.setState({ isLoading: true });

            const merchantResult = await clientApiService.AddMerchantOidcAccess({
                merchantId: this.props.merchantId!,
                merchantRedirectUrls: validRedirectUrls,
                postLogoutRedirectUris: validPostLogoutUrls,
                allowedCorsOrigins: validCorsOrigins
            });

            if (!merchantResult.success) {
                this.setState({ isLoading: false, oidcErrorMessage: merchantResult.message });
                toastService.error(merchantResult.message);
                setTimeout(() => {
                    this.setState({ oidcErrorMessage: "" });
                }, 3000);
                return;
            }

            this.setState({ hasOidcAccess: true, isLoading: false, showOidcDialog: false });
            toastService.success("OIDC client created successfully");
            window.location.reload();
        } catch (error) {
            this.setState({ showOidcDialog: false, isLoading: false });
            toastService.error("Failed to create OIDC client");
        }
    };

    externalKycTemplate = (rowData: any) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }}>
                <div style={{ flex: 1 }}>
                    <InputSwitch checked={this.props.isExternalKyc} onChange={(e) => this.props.onChangeKycSwitch(e.target.value)} />
                </div>
            </div>
        );
    };

    pepDeclarationTemplate = (rowData: any) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }}>
                <div style={{ flex: 1 }}>
                    <InputSwitch checked={this.props.isPepDeclaration} onChange={(e) => this.props.onChangePepDeclarationSwitch(e.target.value)} />
                </div>
            </div>
        );
    };

    multiMerchantIban = (rowData: any) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }}>
                <div style={{ flex: 1 }}>
                    <InputSwitch
                        checked={this.props.allowMultiMerchantIban}
                        onChange={(e) => this.props.onChangeMultiMerchantIbanSwitch(e.target.value)}
                    />
                </div>
            </div>
        );
    };
    multiMerchantClientIban = (rowData: any) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }}>
                <div style={{ flex: 1 }}>
                    <InputSwitch
                        checked={this.props.allowMultiMerchantClientIban}
                        onChange={(e) => this.props.onChangeMultiMerchantClientIbanSwitch(e.target.value)}
                    />
                </div>
            </div>
        );
    };
    isKycOptionalBody = (rowData: any) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }}>
                <div style={{ flex: 1 }}>
                    <InputSwitch
                        checked={this.props.isKycOptional}
                        onChange={(e) => this.props.onChangeisKycOptionalSwitch(e.target.value)}
                        tooltip={`Set true to allow optional KYC, enabling '${this.props.merchant.name ?? "Merchant"}' users to access the dashboard before KYC approval.`}
                        tooltipOptions={{ position: "top" }}
                    />
                </div>
            </div>
        );
    };
    billTariffActiveTemplate = (rowData: any) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }}>
                <div style={{ flex: 1 }}>
                    <InputSwitch
                        checked={this.props.isBillTariffActive}
                        onChange={(e) => this.props.onChangeBillTariffSwitch(e.target.value)}
                    />
                </div>
            </div>
        );
    };
    isCardIssuerTemplate = (rowData: any) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }}>
                <div style={{ flex: 1 }}>
                    <InputSwitch
                        checked={this.props.isCardIssuer}
                        onChange={(e) => this.props.onChangeCardIssuerSwitch(e.target.value)}
                    />
                </div>
            </div>
        );
    };
    isBrtustAppActiveTemplate = (rowData: any) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }}>
                <div style={{ flex: 1 }}>
                    <InputSwitch checked={this.props.isBtrustAppActive} onChange={(e) => this.props.onChangeBtrustAppSwitch(e.target.value)} />
                </div>
            </div>
        );
    };

    isNewRegistrationActiveTemplate = (rowData: any) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }}>
                <div style={{ flex: 1 }}>
                    <InputSwitch
                        checked={this.props.isNewRegistrationActive}
                        onChange={(e) => this.props.onChangeNewRegistrationSwitch(e.target.value)}
                    />
                </div>
            </div>
        );
    };

    render() {
        if (this.state.isLoading) {
            return (
                <AppCard><AppCard.Header title="Change Merchant Client" /><AppCard.Body>
                    <LoadingDots />
                </AppCard.Body></AppCard>
            );
        }

        return (
            <>
                {/* MTLS Dialog */}
                <Dialog
                    header="Generate MTLS Certificate"
                    visible={this.state.showMtlsDialog}
                    style={{ width: "50vw" }}
                    onHide={() => {
                        if (!this.state.showMtlsDialog) return;
                        this.setState({ showMtlsDialog: false, mtlsErrorMessage: "", selectedRole: null, mtlsCertificateValue: "" });
                    }}
                >
                    <div style={{ wordWrap: "break-word" }}>
                        {this.state.mtlsCertificateValue && (
                            <>
                                <p style={{ textAlign: "center", marginBottom: "1rem" }}>Please copy the Certificate. This will not be visible again.</p>
                                <InputTextarea
                                    value={this.state.mtlsCertificateValue}
                                    readOnly
                                    rows={10}
                                    style={{ width: "100%", fontFamily: "monospace", fontSize: "0.85em" }}
                                />
                            </>
                        )}
                        {!this.state.mtlsCertificateValue && (
                            <>
                                <p style={{ textAlign: "center" }}>Please select a role and click below to generate a certificate</p>
                                <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                                    <label style={{ display: "block", marginBottom: "0.5rem" }}>Select Role:</label>
                                    <Dropdown
                                        value={this.state.selectedRole}
                                        options={this.state.availableRoles}
                                        onChange={(e) => this.setState({ selectedRole: e.value })}
                                        optionLabel="name"
                                        placeholder="Select a Role"
                                        style={{ width: "100%" }}
                                    />
                                </div>
                                <div style={{ textAlign: "center", marginTop: "1rem" }}>
                                    <Button
                                        label="Generate"
                                        onClick={this.handleMtlsSubmit}
                                        disabled={!this.state.selectedRole}
                                    />
                                </div>
                            </>
                        )}
                        {this.state.mtlsErrorMessage && (
                            <div style={{ textAlign: "left", marginTop: "1rem", color: "red" }}>
                                <label>Error: {this.state.mtlsErrorMessage}</label>
                            </div>
                        )}
                    </div>
                </Dialog>

                {/* OIDC Dialog */}
                <Dialog
                    header="Configure OIDC Client"
                    visible={this.state.showOidcDialog}
                    style={{ width: "50vw" }}
                    onHide={() => {
                        if (!this.state.showOidcDialog) return;
                        this.setState({
                            showOidcDialog: false,
                            oidcErrorMessage: "",
                            redirectUrls: [""],
                            postLogoutUrls: [""],
                            corsOrigins: [""]
                        });
                    }}
                >
                    <div>
                        <p style={{ textAlign: "center", marginBottom: "1rem" }}>Configure OIDC client settings for mobile app authentication</p>

                        {/* Redirect URLs */}
                        <div style={{ marginBottom: "1.5rem" }}>
                            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>Redirect URLs:</label>
                            {this.state.redirectUrls.map((url, index) => (
                                <div key={index} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                                    <InputText
                                        value={url}
                                        onChange={(e) => {
                                            const newUrls = [...this.state.redirectUrls];
                                            newUrls[index] = e.target.value;
                                            this.setState({ redirectUrls: newUrls });
                                        }}
                                        placeholder="e.g., https://abc.com/auth/callback"
                                        style={{ flex: 1 }}
                                    />
                                    <Button
                                        icon={<X size={16} />}
                                        severity="danger"
                                        onClick={() => {
                                            if (this.state.redirectUrls.length > 1) {
                                                const newUrls = this.state.redirectUrls.filter((_, i) => i !== index);
                                                this.setState({ redirectUrls: newUrls });
                                            }
                                        }}
                                        disabled={this.state.redirectUrls.length === 1}
                                    />
                                </div>
                            ))}
                            <Button
                                icon={<Plus size={16} />}
                                label="Add Redirect URL"
                                size="small"
                                onClick={() => this.setState({ redirectUrls: [...this.state.redirectUrls, ""] })}
                            />
                        </div>

                        {/* Post Logout Redirect URLs */}
                        <div style={{ marginBottom: "1.5rem" }}>
                            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>Post Logout Redirect URLs:</label>
                            {this.state.postLogoutUrls.map((url, index) => (
                                <div key={index} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                                    <InputText
                                        value={url}
                                        onChange={(e) => {
                                            const newUrls = [...this.state.postLogoutUrls];
                                            newUrls[index] = e.target.value;
                                            this.setState({ postLogoutUrls: newUrls });
                                        }}
                                        placeholder="e.g., fastpay://auth/callback"
                                        style={{ flex: 1 }}
                                    />
                                    <Button
                                        icon={<X size={16} />}
                                        severity="danger"
                                        onClick={() => {
                                            if (this.state.postLogoutUrls.length > 1) {
                                                const newUrls = this.state.postLogoutUrls.filter((_, i) => i !== index);
                                                this.setState({ postLogoutUrls: newUrls });
                                            }
                                        }}
                                        disabled={this.state.postLogoutUrls.length === 1}
                                    />
                                </div>
                            ))}
                            <Button
                                icon={<Plus size={16} />}
                                label="Add Post Logout URL"
                                size="small"
                                onClick={() => this.setState({ postLogoutUrls: [...this.state.postLogoutUrls, ""] })}
                            />
                        </div>

                        {/* CORS Origins */}
                        <div style={{ marginBottom: "1.5rem" }}>
                            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>Allowed CORS Origins:</label>
                            {this.state.corsOrigins.map((origin, index) => (
                                <div key={index} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                                    <InputText
                                        value={origin}
                                        onChange={(e) => {
                                            const newOrigins = [...this.state.corsOrigins];
                                            newOrigins[index] = e.target.value;
                                            this.setState({ corsOrigins: newOrigins });
                                        }}
                                        placeholder="e.g., fastpay://"
                                        style={{ flex: 1 }}
                                    />
                                    <Button
                                        icon={<X size={16} />}
                                        severity="danger"
                                        onClick={() => {
                                            if (this.state.corsOrigins.length > 1) {
                                                const newOrigins = this.state.corsOrigins.filter((_, i) => i !== index);
                                                this.setState({ corsOrigins: newOrigins });
                                            }
                                        }}
                                        disabled={this.state.corsOrigins.length === 1}
                                    />
                                </div>
                            ))}
                            <Button
                                icon={<Plus size={16} />}
                                label="Add CORS Origin"
                                size="small"
                                onClick={() => this.setState({ corsOrigins: [...this.state.corsOrigins, ""] })}
                            />
                        </div>

                        <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
                            <Button
                                label="Create OIDC Client"
                                onClick={this.handleOidcSubmit}
                            />
                        </div>

                        {this.state.oidcErrorMessage && (
                            <div style={{ textAlign: "left", marginTop: "1rem", color: "red" }}>
                                <label>Error: {this.state.oidcErrorMessage}</label>
                            </div>
                        )}
                    </div>
                </Dialog>

                <AppCard><AppCard.Header title="Change Merchant Access" /><AppCard.Body className="!p-0">
                    <DataTable value={["ok" as any]} loading={this.state.isLoading}>
                        <Column field="title" header="Title" body={this.titleTemplate}></Column>
                        <Column field="merchantPortal" header="MerchantPortal" body={this.merchantPortalTemplate}></Column>
                        <Column field="mtlsAccess" header="MTLS Access" body={this.MtlsAccessTemplate}></Column>
                        <Column field="oidcAccess" header="OIDC Access" body={this.OidcAccessTemplate}></Column>
                        <Column field="pepDeclaration" header="Pep Declaration" body={this.pepDeclarationTemplate}></Column>
                        <Column field="externalKyc" header="External KYC" body={this.externalKycTemplate}></Column>
                        {this.state.hasExternalKycAccess && (
                            <Column
                                field="isNewRegistrationActive"
                                header="New Registration"
                                body={this.isNewRegistrationActiveTemplate}
                            ></Column>
                        )}
                        {this.state.hasExternalKycAccess && (
                            <Column field="isBtrustAppActive" header="Btrust App" body={this.isBrtustAppActiveTemplate}></Column>
                        )}
                        <Column field="multiMerchantIban" header="Merchant IBAN 2+" body={this.multiMerchantIban}></Column>
                        <Column field="multiMerchantClientIban" header="Merchant Clients IBAN 2+" body={this.multiMerchantClientIban}></Column>
                        <Column field="isKycOptional" header="Split Enrolment" body={this.isKycOptionalBody}></Column>
                        <Column field="isBillTariffActive" header="Bill Tariff" body={this.billTariffActiveTemplate}></Column>
                        <Column field="isCardIssuer" header="Card Issuer" body={this.isCardIssuerTemplate}></Column>
                    </DataTable>
                </AppCard.Body></AppCard>
            </>
        );
    }
}
